import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _isNil from "lodash/isNil";
import _get from "lodash/get";
import _pickBy from "lodash/pickBy";
import _isEmpty from "lodash/isEmpty";
import _omit from "lodash/omit";
import "core-js/modules/es6.regexp.replace.js";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es7.object.entries.js";
import "core-js/modules/es6.string.starts-with.js";
import "core-js/modules/es6.object.keys.js";
import { v4 as uuidV4 } from 'uuid';
import appConfig from '@nerdwallet/app-config';
import { getCookies } from './cookies';
export var CORE_HTTP_HEADERS = {
  COOKIE: 'Cookie',
  REFERRER: 'Referrer',
  USER_AGENT: 'User-Agent',
  X_BROWSER_SESSION_ID: 'X-Browser-Session-ID',
  X_CALLER_CLIENT_ID: 'X-Caller-Client-ID',
  // this should really be X_CLIENT_ID, but we keep it for legacy reasons
  X_CLIENT_VERSION: 'X-Client-Version',
  X_COOKIE_ID: 'X-Cookie-ID',
  X_HEADER_CSRF_TOKEN: 'X-Header-CSRF-Token',
  X_KOUNT_SESSION_ID: 'X-Kount-Session-ID',
  X_MEMBER_IDENTITY_ASSERTION_TOKEN: 'X-Member-Identity-Assertion-Token',
  X_ORIGINATING_APPLICATION_ID: 'X-Originating-Application-ID',
  X_ORIGINATING_IP: 'X-Originating-IP',
  X_ORIGINATING_REFERRER: 'X-Originating-Referrer',
  X_ORIGINATING_URL: 'X-Originating-URL',
  X_ORIGINATING_USER_AGENT: 'X-Originating-User-Agent',
  X_PAGEVIEW_ID: 'X-Pageview-ID',
  X_REFERRING_CALLER_CLIENT_ID: 'X-Referring-Caller-Client-ID',
  X_REQUEST_ID: 'X-Request-ID',
  X_TEST_STATE: 'X-Test-State',
  X_VISITOR_TOKEN: 'X-Visitor-Token',
  X_VISITOR_IDENTITY_ASSERTION_TOKEN: 'X-Visitor-Identity-Assertion-Token'
};
var CORE_HTTP_COOKIES = {
  ACCESS_TOKEN: 'access_token',
  JWT_ACCESS_TOKEN: 'jwt_access_token',
  CSRF_TOKEN: 'csrf_token',
  TEST_STATE: 'test_state',
  VISITOR_TOKEN: 'visitor_token'
};
/**
 * Prefix for WP cookies used for:
 *   1. authed WP API requests
 *   2. bypassing our edge cache
 *
 * @link https://github.com/NerdWallet/edge-configs/blob/2c5684bc4fea9e052d62202dbae2cce7566d5b35/src/templates/location.conf.jinja#L133)
 */

var WP_COOKIE_PREFIX = 'wordpress_';
/**
 * Replaces unsafe control characters in a string.
 */

var replaceControlCharacters = function replaceControlCharacters(str, replace) {
  if (str) {
    // eslint-disable-next-line no-control-regex
    return str.replace(/[\x00-\x1F\x7F]/g, replace);
  }

  return '';
};
/**
 * Return the full URL of the request.
 */


var getRequestUrl = function getRequestUrl(serverRequest) {
  return "".concat(serverRequest.protocol, "://").concat(serverRequest.get('Host')).concat(serverRequest.originalUrl);
};
/**
 * Calls the appropriate function to sanitize a header.
 */


var sanitizeHeader = function sanitizeHeader(header) {
  return replaceControlCharacters(header, ' ');
}; // Safe access to window global properties


function getWindowProp(property) {
  if (typeof window !== 'undefined') {
    return window[property];
  }

  return null;
}
/**
 * Safe access to `window.document.referrer`.
 */


function getDocumentReferrer() {
  if (getWindowProp('document')) {
    return window.document.referrer;
  }

  return null;
}
/**
 * Calls the appropriate functions to sanitize a user defined cookie.
 */


function sanitizeCookie(key, value) {
  // Key sanitization is just precautionary, there should never be a case where a user
  // is given the ability to send cookies with arbitrary keys to services
  var cookieKey = replaceControlCharacters(key, ' ').replace(/;/g, '%3B');
  var cookieValue = replaceControlCharacters(value, ' ').replace(/;/g, '%3B');
  return "".concat(cookieKey, "=").concat(cookieValue);
}
/**
 * Given a cookie object returns a string for the Cookie header.
 */


function buildCookieString(cookies) {
  return Object.entries(cookies).map(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
        name = _ref2[0],
        value = _ref2[1];

    return sanitizeCookie(name, value !== null && value !== void 0 ? value : '');
  }).join('; ');
}
/**
 * Return the `Cookie` header.
 */


var getCookieHeader = function getCookieHeader(_ref3) {
  var coreCookies = _ref3.coreCookies,
      inputCookies = _ref3.inputCookies;

  var allCookies = _omit(_objectSpread(_objectSpread({}, inputCookies), coreCookies), // The `test_state` cookie does not need to be propogated because it will be sent in the request header `X-Test-State`
  [CORE_HTTP_COOKIES.TEST_STATE]); // Set the cookie header, except for in browsers and mobile (when process.browser
  // is present). Browsers and mobile don't allow setting the cookie header, they
  // automatically manage cookies.


  if (typeof window === 'undefined' && !_isEmpty(allCookies)) {
    return buildCookieString(allCookies);
  }

  return null;
};
/**
 * Return the `X-Browser-Session-ID` header.
 */


var getBrowserSessionID = function getBrowserSessionID(context) {
  // Get the unsigned browser session id for the request header in the case that the client
  // doesn't have a valid browser_session_id in their cookies. This is used for mobile where
  // theres no initial request to www. for nginx to return a Set-Cookie header with a signed
  // browser session id.
  if (context.config && context.config.getBrowserSessionIdOverride) {
    return context.config.getBrowserSessionIdOverride(context);
  }

  if (context.serverRequest) {
    return context.serverRequest.get(CORE_HTTP_HEADERS.X_BROWSER_SESSION_ID);
  }

  return null;
};
/**
 * Return the `X-Cookie-ID` header.
 */


var getCookieID = function getCookieID(context) {
  // Get the unsigned cookie id for the request header in the case that the client doesn't have
  // a valid cookie_id in their cookies. This is used for mobile where theres no initial
  // request to www. for nginx to return a Set-Cookie header with a signed cookie id.
  if (context.config && context.config.getCookieIdOverride) {
    return context.config.getCookieIdOverride(context);
  }

  if (context.serverRequest) {
    return context.serverRequest.get(CORE_HTTP_HEADERS.X_COOKIE_ID);
  } // In a browser, we rely on the `cookie_id` cookie to be sent along with the request.
  // The edge is responsible for setting the `X-Cookie-ID` header on inbound requests.


  return null;
};
/**
 * Return the CSRF Token.
 * Check the cookie value before the header so that attackers can not hijack another valid CSRF Token.
 * https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/589072198/Quick+Intro+to+CSRF+Tokens+at+NerdWallet
 */


var getCsrfToken = function getCsrfToken(_ref4) {
  var _coreCookies$CORE_HTT;

  var serverRequest = _ref4.serverRequest,
      coreCookies = _ref4.coreCookies;
  return (_coreCookies$CORE_HTT = coreCookies[CORE_HTTP_COOKIES.CSRF_TOKEN]) !== null && _coreCookies$CORE_HTT !== void 0 ? _coreCookies$CORE_HTT : serverRequest === null || serverRequest === void 0 ? void 0 : serverRequest.get(CORE_HTTP_HEADERS.X_HEADER_CSRF_TOKEN);
};
/**
 * Return the `X-Originating-IP` header.
 */


var getOriginatingIP = function getOriginatingIP(_ref5) {
  var serverRequest = _ref5.serverRequest;

  if (serverRequest) {
    return serverRequest.ip;
  }

  return null;
};
/**
 * Return the `X-Originating-Referrer` header.
 */


var getOriginatingReferrer = function getOriginatingReferrer(_ref6) {
  var config = _ref6.config,
      serverRequest = _ref6.serverRequest;

  if (serverRequest) {
    // Forward `X-Originating-Referrer` if it exists, otherwise look at the request's referrer.
    return serverRequest.get(CORE_HTTP_HEADERS.X_ORIGINATING_REFERRER) || serverRequest.get(CORE_HTTP_HEADERS.REFERRER);
  }

  if (config && config.nwa) {
    return config.nwa.getGlobalProp('referrer') || getDocumentReferrer();
  }

  return null;
};
/**
 * Return the `X-Request-ID` header.
 */


var getRequestID = function getRequestID(_ref7) {
  var serverRequest = _ref7.serverRequest;

  if (serverRequest) {
    var requestId = serverRequest.get(CORE_HTTP_HEADERS.X_REQUEST_ID);

    if (requestId) {
      return requestId;
    }
  } // Generate a new UUID if one was not present.


  return uuidV4();
};
/**
 * Return the `X-Originating-URL` header.
 */


var getOriginatingURL = function getOriginatingURL(_ref8) {
  var config = _ref8.config,
      serverRequest = _ref8.serverRequest;

  if (config.getUrl) {
    return config.getUrl();
  }

  if (serverRequest) {
    // Forward `X-Originating-URL` if it exists, otherwise look at the request url.
    return serverRequest.get(CORE_HTTP_HEADERS.X_ORIGINATING_URL) || getRequestUrl(serverRequest);
  }

  if (config && config.nwa) {
    return config.nwa.getGlobalProp('url');
  }

  if (getWindowProp('location')) {
    return window.location.href;
  }

  return null;
};
/**
 * Return the `X-Originating-User-Agent` header.
 */


var getOriginatingUserAgent = function getOriginatingUserAgent(_ref9) {
  var serverRequest = _ref9.serverRequest;

  // Forward `X-Originating-User-Agent` if it exists, otherwise look at the request's referrer.
  if (serverRequest) {
    return serverRequest.get(CORE_HTTP_HEADERS.X_ORIGINATING_USER_AGENT) || serverRequest.get(CORE_HTTP_HEADERS.USER_AGENT);
  }

  if (getWindowProp('navigator')) {
    return window.navigator.userAgent;
  }

  return null;
};
/**
 * Return the `X-Pageview-ID` header.
 */


var getPageviewID = function getPageviewID(_ref10) {
  var config = _ref10.config;

  if (config.getPageviewId) {
    return config.getPageviewId();
  }

  if (config && config.nwa) {
    return config.nwa.getGlobalProp('pageViewId');
  }

  return null;
};
/**
 * Return the `X-Test-State` header.
 *
 * Propagate the test state header if this is a server request.
 * Part of the "QA Test States" protocol - see https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/515539477/2018-12-20+QA+Test+States
 * TODO(UDC-4431) - Replace project spec with cleaner docs once complete.
 */


var getTestState = function getTestState(_ref11) {
  var serverRequest = _ref11.serverRequest,
      coreCookies = _ref11.coreCookies;

  if (serverRequest && serverRequest.get(CORE_HTTP_HEADERS.X_TEST_STATE)) {
    return serverRequest.get(CORE_HTTP_HEADERS.X_TEST_STATE);
  }

  return coreCookies[CORE_HTTP_COOKIES.TEST_STATE];
};
/**
 * Return the `X-Vistor-Token` header.
 */


var getVisitorToken = function getVisitorToken(_ref12) {
  var serverRequest = _ref12.serverRequest;

  if (serverRequest) {
    return serverRequest.get('X-Visitor-Token');
  }

  return null;
};
/**
 * Return the `X-Member-Identity-Assertion-Token` header.
 */


var getMemberIdentityAssertionToken = function getMemberIdentityAssertionToken(_ref13) {
  var serverRequest = _ref13.serverRequest;

  if (serverRequest) {
    return serverRequest.get(CORE_HTTP_HEADERS.X_MEMBER_IDENTITY_ASSERTION_TOKEN);
  } // On clients, the member_identity_assertion_token cookie will
  // automatically be sent, so no header is needed.


  return null;
};
/**
 * Return the `X-Visitor-Identity-Assertion-Token` header.
 */


var getVisitorIdentityAssertionToken = function getVisitorIdentityAssertionToken(_ref14) {
  var serverRequest = _ref14.serverRequest;

  if (serverRequest) {
    return serverRequest.get(CORE_HTTP_HEADERS.X_VISITOR_IDENTITY_ASSERTION_TOKEN);
  } // On clients, the visitor_identity_assertion_token cookie will
  // automatically be sent, so no header is needed.


  return null;
};
/**
 * Return WP cookies via the `getCookies` helper.
 */


var pickWpCookies = function pickWpCookies(existingCookies) {
  return _pickBy(existingCookies, function (value, key) {
    return key.indexOf(WP_COOKIE_PREFIX) === 0;
  });
};
/**
 * Return the core cookies via the `getCookies` helper.
 */


var getCoreCookies = function getCoreCookies(context) {
  return getCookies(context).then(function (existingCookies) {
    var _pickBy2;

    var coreCookies = _pickBy((_pickBy2 = {}, _defineProperty(_pickBy2, CORE_HTTP_COOKIES.ACCESS_TOKEN, existingCookies[CORE_HTTP_COOKIES.ACCESS_TOKEN]), _defineProperty(_pickBy2, CORE_HTTP_COOKIES.JWT_ACCESS_TOKEN, existingCookies[CORE_HTTP_COOKIES.JWT_ACCESS_TOKEN]), _defineProperty(_pickBy2, CORE_HTTP_COOKIES.CSRF_TOKEN, existingCookies[CORE_HTTP_COOKIES.CSRF_TOKEN]), _defineProperty(_pickBy2, CORE_HTTP_COOKIES.TEST_STATE, existingCookies[CORE_HTTP_COOKIES.TEST_STATE]), _defineProperty(_pickBy2, CORE_HTTP_COOKIES.VISITOR_TOKEN, existingCookies[CORE_HTTP_COOKIES.VISITOR_TOKEN]), _pickBy2));

    var wpCookies = pickWpCookies(existingCookies);
    return _objectSpread(_objectSpread({}, coreCookies), wpCookies);
  });
};
/**
 * Returns an object with all of the core http headers set.
 */


export var getCoreHTTPHeaders = function getCoreHTTPHeaders(_ref15) {
  var url = _ref15.url,
      config = _ref15.config,
      serverRequest = _ref15.serverRequest,
      inputCookies = _ref15.cookies,
      referringClientID = _ref15.referringClientID,
      _ref15$kountSessionID = _ref15.kountSessionID,
      kountSessionID = _ref15$kountSessionID === void 0 ? getWindowProp('__KOUNT_SESSION_ID__') : _ref15$kountSessionID;
  return getCoreCookies({
    url: url,
    config: config,
    serverRequest: serverRequest
  }).then(function (coreCookies) {
    var _headers, _serverRequest$header;

    var headers = (_headers = {}, _defineProperty(_headers, CORE_HTTP_HEADERS.COOKIE, getCookieHeader({
      coreCookies: coreCookies,
      inputCookies: inputCookies
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_BROWSER_SESSION_ID, getBrowserSessionID({
      url: url,
      config: config,
      serverRequest: serverRequest
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_CALLER_CLIENT_ID, config.clientID), _defineProperty(_headers, CORE_HTTP_HEADERS.X_CLIENT_VERSION, _get(appConfig, '_meta.version')), _defineProperty(_headers, CORE_HTTP_HEADERS.X_COOKIE_ID, getCookieID({
      config: config,
      serverRequest: serverRequest
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_HEADER_CSRF_TOKEN, getCsrfToken({
      serverRequest: serverRequest,
      coreCookies: coreCookies
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_KOUNT_SESSION_ID, kountSessionID), _defineProperty(_headers, CORE_HTTP_HEADERS.X_MEMBER_IDENTITY_ASSERTION_TOKEN, getMemberIdentityAssertionToken({
      serverRequest: serverRequest
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_ORIGINATING_APPLICATION_ID, config.applicationID), _defineProperty(_headers, CORE_HTTP_HEADERS.X_ORIGINATING_IP, getOriginatingIP({
      serverRequest: serverRequest
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_ORIGINATING_REFERRER, getOriginatingReferrer({
      config: config,
      serverRequest: serverRequest
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_ORIGINATING_URL, getOriginatingURL({
      serverRequest: serverRequest,
      config: config
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_ORIGINATING_USER_AGENT, getOriginatingUserAgent({
      serverRequest: serverRequest
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_PAGEVIEW_ID, getPageviewID({
      config: config
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_REFERRING_CALLER_CLIENT_ID, referringClientID), _defineProperty(_headers, CORE_HTTP_HEADERS.X_REQUEST_ID, getRequestID({
      serverRequest: serverRequest
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_TEST_STATE, getTestState({
      serverRequest: serverRequest,
      coreCookies: coreCookies
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_VISITOR_TOKEN, getVisitorToken({
      serverRequest: serverRequest
    })), _defineProperty(_headers, CORE_HTTP_HEADERS.X_VISITOR_IDENTITY_ASSERTION_TOKEN, getVisitorIdentityAssertionToken({
      serverRequest: serverRequest
    })), _headers);

    var abAndffHeaders = _pickBy((_serverRequest$header = serverRequest === null || serverRequest === void 0 ? void 0 : serverRequest.headers) !== null && _serverRequest$header !== void 0 ? _serverRequest$header : {}, function (val, h) {
      return h.startsWith('test_') || h.startsWith('feat_');
    });

    var allHeaders = _objectSpread(_objectSpread({}, headers), abAndffHeaders); // Sanitize all headers


    return Object.keys(allHeaders).reduce(function (accumulator, headerName) {
      // Remove any values that are `null` or `undefined`.
      // Node and browsers normally ignore null header values, but React Native's Android HTTP
      // implementation will crash if a null header value is present.
      if (!_isNil(allHeaders[headerName])) {
        accumulator[headerName] = sanitizeHeader(allHeaders[headerName]);
      }

      return accumulator;
    }, {});
  });
};